import { Capacitor } from '@capacitor/core';
import { GetBrightnessReturnValue } from '@capacitor-community/screen-brightness';

const bridge = () => (window as any).NativeBridge;
const isLocalTest = import.meta.env.DEV && import.meta.env.VITE_IS_MOBILE_LOCAL === 'true';
const isExternal = () => !Capacitor.isPluginAvailable('SecureStoragePlugin') || isLocalTest;
const isAndroid = Capacitor.getPlatform() === 'android';

declare global {
  interface Window {
    _onAppStateChange?: (isActive: boolean) => void;
    _onBackButton?: (canGoBack: boolean) => void;
  }
}

export const brightnessSet = async ({ brightness }: { brightness: number }) => {
  if (isExternal() || isAndroid) {
    try {
      bridge().setBrightness(brightness);
    } catch (error) {
      throw Error('brightness set 실패' + error);
    }
  } else {
    const { ScreenBrightness } = await import('@capacitor-community/screen-brightness');
    await ScreenBrightness.setBrightness({ brightness });
  }
};

export const brightnessGet = async (): Promise<GetBrightnessReturnValue> => {
  if (isExternal() || isAndroid) {
    try {
      const brightness = bridge().getBrightness();
      return { brightness };
    } catch (error) {
      throw Error('brightness get 실패' + error);
    }
  } else {
    const { ScreenBrightness } = await import('@capacitor-community/screen-brightness');
    const brightness = await ScreenBrightness.getBrightness();
    return brightness;
  }
};

export const keepAwake = async () => {
  if (isExternal() || isAndroid) {
    try {
      bridge().keepAwake();
    } catch (error) {
      throw Error('native bridge 에서 keep awake 실패' + error);
    }
  } else {
    const { KeepAwake } = await import('@capacitor-community/keep-awake');
    await KeepAwake.keepAwake();
  }
};

export const allowSleep = async () => {
  if (isExternal() || isAndroid) {
    try {
      bridge().allowSleep();
    } catch (error) {
      throw Error('native bridge 에서 allow sleep 실패' + error);
    }
  } else {
    const { KeepAwake } = await import('@capacitor-community/keep-awake');
    await KeepAwake.allowSleep();
  }
};

export const secureStorageSet = async ({ key, value }: { key: string; value: string }): Promise<void> => {
  if (isExternal() || isAndroid) {
    try {
      bridge().setSecureStorage(key, value);
    } catch {
      console.error('secure storage set이 실패');
    }
  } else {
    const { SecureStoragePlugin } = await import('capacitor-secure-storage-plugin');
    await SecureStoragePlugin.set({ key, value });
  }
};

export const secureStorageGet = async ({ key }: { key: string }): Promise<string> => {
  if (isExternal() || isAndroid) {
    const value = bridge().getSecureStorage(key);
    if (!value) throw new Error(`secure storage 값이 null 입니다. key: ${key}`);

    return value;
  } else {
    const { SecureStoragePlugin } = await import('capacitor-secure-storage-plugin');
    const { value } = await SecureStoragePlugin.get({ key });
    if (!value) throw new Error(`secure storage 값이 null 입니다. key: ${key}`);

    return value;
  }
};

export const secureStorageRemove = async ({ key }: { key: string }): Promise<void> => {
  if (isExternal() || isAndroid) {
    bridge().removeSecureStorage(key);
  } else {
    const { SecureStoragePlugin } = await import('capacitor-secure-storage-plugin');
    await SecureStoragePlugin.remove({ key });
  }
};

export const addAppStateChangeListener = async (callback: (isActive: boolean) => void): Promise<void> => {
  if (isExternal() || isAndroid) {
    window._onAppStateChange = callback;
  } else {
    const { App } = await import('@capacitor/app');
    await App.addListener('appStateChange', ({ isActive }) => callback(isActive));
  }
};

export const removeAppStateChangeListener = () => {
  window._onAppStateChange = undefined;
};

export const deviceGetLanguageCode = async () => {
  if (isExternal() || isAndroid) {
    return bridge().getLanguageCode();
  } else {
    const { Device } = await import('@capacitor/device');
    const languageCode = await Device.getLanguageCode();
    return languageCode.value;
  }
};

export const exitApp = async (): Promise<void> => {
  if (isExternal() || isAndroid) {
    bridge().exitApp();
  } else {
    const { App } = await import('@capacitor/app');
    App.exitApp();
  }
};

export const addBackButtonListener = async (callback: (canGoBack: boolean) => void): Promise<void> => {
  if (isExternal() || isAndroid) {
    window._onBackButton = callback;
  } else {
    const { App } = await import('@capacitor/app');
    await App.addListener('backButton', ({ canGoBack }) => callback(canGoBack));
  }
};

export const removeBackButtonListener = () => {
  window._onBackButton = undefined;
};

export const navigateInApp = (url: string) => {
  if (bridge()) {
    bridge().loadUrl(url);
  } else {
    window.location.href = url;
  }
};
